import Hls from 'hls.js'

const STATE = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  PLAYING: 'playing',
  BUFFERING: 'buffering',
  RECOVERING: 'recovering',
  FAILED: 'failed',
  ENDED: 'ended',
}

const RECOVER_MAX_RETRY = 3

// 44 字节的空 WAV（只有文件头、没有采样），用来在用户手势里「点亮」音频元素
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='

// 已在用户手势里解锁、等待被 HLS 接管的音频元素
let primedAudio = null

/**
 * 在用户手势的**同步调用栈**里预先解锁一个 audio 元素。
 *
 * iOS Safari 只认「play() 发生在用户手势的同步栈里」，而 HLS 的启动链是
 * 点击 → 路由跳转 → getPaperDetails → getQuesList → 拉 anchors.json →
 * 等 m3u8 清单解析 → 才 play()，中间隔着两三个网络请求，手势早就失效了。
 * 表现就是：控制中心里音频已就绪、时长也对，但停在 0:00 不动。
 *
 * 元素级授权无法转移，所以只能提前占一个元素、在手势里点亮它，
 * 等 HLS 起来时直接接管这个已授权的元素。
 * PC / 安卓没有这个限制，这段对它们只是多创建一个空元素。
 */
export function primeHlsAudio() {
  if (primedAudio) return primedAudio
  try {
    const audio = document.createElement('audio')
    audio.preload = 'auto'
    audio.crossOrigin = 'anonymous'
    // 必须有可播放的 src，光调 play() 而没有源不会让 iOS 记住授权
    audio.src = SILENT_WAV
    const promise = audio.play()
    if (promise && promise.catch) promise.catch(() => {})
    audio.pause()
    audio.currentTime = 0
    primedAudio = audio
    return audio
  } catch (e) {
    return null
  }
}

// 取走预热元素（一次性，取后置空，等下一次用户交互再补）
function takePrimedAudio() {
  const audio = primedAudio
  primedAudio = null
  if (audio) {
    // 清掉静音源，交给调用方重新挂 HLS
    audio.removeAttribute('src')
    audio.load()
  }
  return audio
}

class HlsAudioManager {
  constructor(options = {}) {
    this._audio = null
    this._hls = null
    this._url = null
    this._state = STATE.IDLE
    this._retryCount = 0
    this._bufferingTimer = null
    this._lastCurrentTime = 0
    this._bufferingStartAt = 0

    this._onTimeUpdate = options.onTimeUpdate || (() => {})
    this._onStateChange = options.onStateChange || (() => {})
    this._onError = options.onError || (() => {})
    this._onEnded = options.onEnded || (() => {})
    this._shouldPlayAfterManifest = false

    /**
     * 这里刻意不建元素：load() 一进来就 destroy() 再重建，构造时建的那个必然被丢掉。
     * 留到 load() 里建，预热过的元素才不会被白白消耗掉一个。
     */
  }

  _createAudioElement() {
    // 优先接管在用户手势里点亮过的元素（见 primeHlsAudio 的说明）
    this._audio = takePrimedAudio() || document.createElement('audio')
    this._audio.preload = 'auto'
    this._audio.crossOrigin = 'anonymous'

    this._audio.addEventListener('timeupdate', () => {
      if (!this._audio) return
      this._onTimeUpdate(this._audio.currentTime)
    })

    this._audio.addEventListener('waiting', () => {
      if (!this._audio) return
      this._setState(STATE.BUFFERING)
      this._startBufferingTimer()
    })

    this._audio.addEventListener('playing', () => {
      if (!this._audio) return
      this._clearBufferingTimer()
      this._setState(STATE.PLAYING)
    })

    this._audio.addEventListener('pause', () => {
      if (!this._audio) return
      this._clearBufferingTimer()
    })

    this._audio.addEventListener('ended', () => {
      if (!this._audio) return
      this._setState(STATE.ENDED)
      this._onEnded()
    })

    this._audio.addEventListener('error', () => {
      if (!this._audio) return
      this._handleMediaError()
    })
  }

  _setState(newState) {
    if (this._state === newState) return
    this._state = newState
    this._onStateChange(newState)
  }

  _startBufferingTimer() {
    this._clearBufferingTimer()
    this._bufferingStartAt = Date.now()
    this._bufferingTimer = setInterval(() => {
      const elapsed = Date.now() - this._bufferingStartAt
      if (elapsed > 5000 && this._state === STATE.BUFFERING) {
        this._recover()
      }
    }, 1000)
  }

  _clearBufferingTimer() {
    if (this._bufferingTimer) {
      clearInterval(this._bufferingTimer)
      this._bufferingTimer = null
    }
  }

  _handleMediaError() {
    if (this._hls && Hls.isSupported()) {
      this._setState(STATE.RECOVERING)
      this._hls.recoverMediaError()
    } else {
      this._setState(STATE.FAILED)
      this._onError({ type: 'media_error', fatal: true })
    }
  }

  _recover() {
    if (this._retryCount >= RECOVER_MAX_RETRY) {
      this._setState(STATE.FAILED)
      this._onError({ type: 'recover_failed', fatal: true })
      return
    }
    this._retryCount++
    this._setState(STATE.RECOVERING)

    if (this._hls && Hls.isSupported()) {
      this._hls.recoverMediaError()
    } else {
      this.seek(this._lastCurrentTime)
      this.play()
    }
  }

  load(url, startTime = 0) {
    if (!url) return
    this._url = url
    this._startTime = startTime
    this._shouldPlayAfterManifest = false
    this.destroy()
    this._createAudioElement()
    this._setState(STATE.LOADING)

    if (Hls.isSupported()) {
      this._hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
      })
      this._hls.loadSource(url)
      this._hls.attachMedia(this._audio)

      this._hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this._setState(STATE.READY)
        this._retryCount = 0
        if (this._startTime > 0) {
          // 继续考试/seek 场景：先设置 currentTime，seek 完成后再 play
          const doPlay = () => {
            if (this._shouldPlayAfterManifest) {
              this._shouldPlayAfterManifest = false
              this.play()
            }
          }
          if (this._audio.readyState >= 2) {
            this._audio.currentTime = this._startTime
            doPlay()
          } else {
            const doSeek = () => {
              this._audio.currentTime = this._startTime
              this._audio.removeEventListener('loadedmetadata', doSeek)
              doPlay()
            }
            this._audio.addEventListener('loadedmetadata', doSeek)
          }
        } else {
          // 首次进入考试 startTime=0：保持原有逻辑，避免改动引入异常
          if (this._shouldPlayAfterManifest) {
            this._shouldPlayAfterManifest = false
            this.play()
          }
        }
      })

      this._hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              this._setState(STATE.BUFFERING)
              this._startBufferingTimer()
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              this._handleMediaError()
              break
            default:
              this._setState(STATE.FAILED)
              this._onError({ type: data.type, fatal: true, details: data.details })
              break
          }
        }
      })
    } else if (this._audio.canPlayType('application/vnd.apple.mpegurl')) {
      this._audio.src = url
      this._audio.addEventListener('loadedmetadata', () => {
        this._setState(STATE.READY)
        if (this._startTime > 0) {
          this.seek(this._startTime)
        }
      })
    } else {
      this._setState(STATE.FAILED)
      this._onError({ type: 'not_supported', fatal: true })
    }
  }

  play() {
    if (!this._audio) return
    // 如果 m3u8 还没解析完成，先标记，等 MANIFEST_PARSED 后再播放
    if (this._state === STATE.LOADING) {
      this._shouldPlayAfterManifest = true
      return
    }
    const promise = this._audio.play()
    if (promise && promise.catch) {
      promise.catch((err) => {
        console.warn('HLS play error:', err)
      })
    }
  }

  pause() {
    if (this._audio) {
      this._audio.pause()
    }
  }

  stop() {
    this.pause()
    if (this._audio) {
      this._audio.currentTime = 0
      this._audio.src = ''
    }
  }

  seek(time) {
    if (!this._audio) return
    this._lastCurrentTime = time
    if (this._audio.readyState >= 2) {
      this._audio.currentTime = time
    } else {
      const doSeek = () => {
        this._audio.currentTime = time
        this._audio.removeEventListener('loadedmetadata', doSeek)
      }
      this._audio.addEventListener('loadedmetadata', doSeek)
    }
  }

  resume() {
    this._retryCount = 0
    this.play()
  }

  get currentTime() {
    return this._audio ? this._audio.currentTime : 0
  }

  // 供锚点片段播放使用：需要自行控制 seek 与 play 的先后时序
  get media() {
    return this._audio
  }

  get duration() {
    return this._audio ? this._audio.duration : 0
  }

  get state() {
    return this._state
  }

  destroy() {
    this._clearBufferingTimer()
    if (this._hls) {
      this._hls.destroy()
      this._hls = null
    }
    if (this._audio) {
      this._audio.pause()
      this._audio.src = ''
      this._audio = null
    }
    this._state = STATE.IDLE
  }
}

export default HlsAudioManager
export { STATE }
